import { DocumentUploadFileState, DocumentUploadIssueSeverity, DocumentUploadIssueViewModel } from 'src/app/shared/models/autogenerated-piping';
import { FILE_NAME_COLUMN_KEY, normalizeFileName } from './bu-pdf-bulk-upload.constants';

/** An index row being edited; `values` is aligned with the preview columns. */
export interface PreviewRow {
  rowId: number;
  values: string[];
}

/** A document as the page shows it: the server's state, adjusted by what this page is doing with the file right now. */
export interface DisplayFile {
  fileName: string;
  sizeInBytes: number;
  state: DocumentUploadFileState;
  /** True while the browser is sending this file. */
  isUploading: boolean;
  message?: string;
}

/** Where one file the user selected in this page session stands. Every state except `uploading` is terminal. */
export type AttemptState = 'uploading' | 'uploaded' | 'rejected' | 'failed';

/** What this page knows about one file the user selected, keyed by its normalized name. Lives only as long as the page. */
export interface UploadAttempt {
  fileName: string;
  sizeInBytes: number;
  state: AttemptState;
  message?: string;
  /** True when the file was refused because another selected file has the same name. */
  isRepeatedName?: boolean;
}

/** The state of the file a row refers to. `none` means the row names no file at all. */
export type RowFileState = 'none' | 'missing' | 'uploading' | 'uploaded' | 'rejected' | 'failed';

export type RowSeverity = 'error' | 'warning' | 'none';

export type SortDirection = 'asc' | 'desc' | '';

/** What the user asked the table to sort by; an empty direction means the default order (problems first). */
export interface RowSort {
  active: string;
  direction: SortDirection;
}

/** The part of one row that the table renders, computed once per change instead of once per cell. */
export interface RowView {
  row: PreviewRow;
  /** The row's worksheet row number in the index file as Excel shows it (the row ID, header row included); never changes with sorting or edits. */
  excelRow: number;
  fileName: string;
  fileKey: string;
  fileState: RowFileState;
  fileMessage: string;
  /** How many rows refer to the same file name (1 when it is used by one row only). */
  sharedCount: number;
  /** Errors and warnings about the row's values; the file column is judged by the file state instead. */
  dataErrorCount: number;
  dataWarningCount: number;
  /** True when the row creates a new release of a current record; information only, it never needs correcting. */
  isNewRevision: boolean;
  /** The server's explanation of the new revision, for the row's tooltip. */
  revisionMessage: string;
  severity: RowSeverity;
  /** 0 data errors, 1 no uploaded file, 2 rejected or failed file, 3 warnings, 4 new revision, 5 healthy. */
  rank: number;
  statusLabel: string;
  statusIcon: string;
}

/** Whether every file attempt of the page reached an end, and how it ended. */
export interface ProcessingProgress {
  attemptedCount: number;
  uploadedCount: number;
  rejectedCount: number;
  failedCount: number;
  uploadingCount: number;
  processedCount: number;
  needsAttentionCount: number;
  activeRequestCount: number;
  isFinished: boolean;
}

/** Whether the files the index needs are uploaded. Unrelated to how many files the user selected. */
export interface RequiredProgress {
  requiredFileCount: number;
  requiredUploadedCount: number;
  areAllUploaded: boolean;
}

/** The counts the review status is made of. */
export interface ReviewInputs {
  rowsNeedingFile: number;
  rowsWithErrors: number;
  warningCount: number;
  filesFailed: number;
  filesNotValid: number;
  filesNotReferenced: number;
  filesRepeated: number;
  filesNoLongerUsed: number;
  otherIssues: number;
  filesUploading: number;
}

export interface ReviewCategory {
  id: string;
  severity: 'error' | 'warning' | 'info';
  count: number;
  text: string;
}

export interface ReviewSummary {
  categories: ReviewCategory[];
  /** Number of items that need the user's attention; what the heading says. */
  issueCount: number;
  /** True only when every category is empty, including files that are still being sent. */
  isHealthy: boolean;
}

/** The wording the page shows for file problems. Internal states never reach the user; they are mapped to these. */
export const FILE_MESSAGES = {
  noFileSelected: 'No file is associated with this row. Select and upload a file before confirming.',
  missing: 'No file has been uploaded for this row.',
  notUploaded: (fileName: string): string => `The file '${fileName}' has not been uploaded successfully. Select the file before confirming.`,
  notReferenced: 'This file is not referenced by the index and was not uploaded.',
  notValid: 'The selected file is not valid.',
  failed: 'The file could not be uploaded. Try again.',
  uploading: 'Uploading...',
};

const REPEATED_NAME_PREFIX: string = 'More than one selected file has the name';

/** The label shown next to a row's file, never relying on color. */
export const ROW_FILE_STATE_LABELS: Record<RowFileState, string> = {
  none: 'Missing',
  missing: 'Missing',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  rejected: 'Not valid',
  failed: 'Upload failed',
};
/** The icon shown next to a row's file state, always together with the label. */
export const ROW_FILE_STATE_ICONS: Record<RowFileState, string> = {
  none: 'attach_file',
  missing: 'attach_file',
  uploading: 'sync',
  uploaded: 'check_circle',
  rejected: 'block',
  failed: 'cloud_off',
};

/**
 * Turns the answer of the server for a rejected file into wording for the user.
 * A file whose name no row uses was not uploaded at all; a file the index does use was refused for its content.
 * @param serverMessage The server's explanation, if any.
 * @param isReferenced True when a row of the index uses the file name.
 */
export function describeRejection(serverMessage: string | undefined, isReferenced: boolean): string {
  if (serverMessage?.startsWith(REPEATED_NAME_PREFIX)) {
    return serverMessage;
  }
  if (!isReferenced) {
    return FILE_MESSAGES.notReferenced;
  }
  const detail: string = (serverMessage ?? '').trim();
  return detail.length > 0 && detail !== FILE_MESSAGES.notReferenced ? `${FILE_MESSAGES.notValid} ${detail}` : FILE_MESSAGES.notValid;
}

/**
 * Counts how the file attempts of this page ended.
 * Rejected and failed attempts are finished too: they need attention, but they never keep the processing unfinished.
 * @param attempts Every file the user selected in this page session (the latest attempt per file name).
 * @param activeRequestCount Document requests still running.
 */
export function computeProcessingProgress(attempts: Iterable<UploadAttempt>, activeRequestCount: number): ProcessingProgress {
  let uploadedCount: number = 0;
  let rejectedCount: number = 0;
  let failedCount: number = 0;
  let uploadingCount: number = 0;
  for (const attempt of attempts) {
    switch (attempt.state) {
      case 'uploaded':
        uploadedCount++;
        break;
      case 'rejected':
        rejectedCount++;
        break;
      case 'failed':
        failedCount++;
        break;
      default:
        uploadingCount++;
    }
  }

  const attemptedCount: number = uploadedCount + rejectedCount + failedCount + uploadingCount;
  const processedCount: number = uploadedCount + rejectedCount + failedCount;
  return {
    attemptedCount,
    uploadedCount,
    rejectedCount,
    failedCount,
    uploadingCount,
    processedCount,
    needsAttentionCount: rejectedCount + failedCount,
    activeRequestCount,
    isFinished: attemptedCount > 0 && processedCount === attemptedCount && activeRequestCount === 0,
  };
}

/**
 * Counts how many of the files the index needs are uploaded.
 * @param requiredKeys The distinct normalized file names the rows refer to.
 * @param isUploaded Tells whether a file name has one current, successfully uploaded file.
 */
export function computeRequiredProgress(requiredKeys: ReadonlySet<string>, isUploaded: (fileKey: string) => boolean): RequiredProgress {
  let requiredUploadedCount: number = 0;
  for (const key of requiredKeys) {
    if (isUploaded(key)) {
      requiredUploadedCount++;
    }
  }
  return {
    requiredFileCount: requiredKeys.size,
    requiredUploadedCount,
    areAllUploaded: requiredUploadedCount === requiredKeys.size,
  };
}

/** The state of the file a row refers to, from what the page knows about that file. */
export function toRowFileState(hasFileName: boolean, file: DisplayFile | undefined): RowFileState {
  if (!hasFileName) {
    return 'none';
  }
  if (!file) {
    return 'missing';
  }
  if (file.isUploading) {
    return 'uploading';
  }
  switch (file.state) {
    case DocumentUploadFileState.Uploaded:
      return 'uploaded';
    case DocumentUploadFileState.Failed:
      return 'failed';
    case DocumentUploadFileState.Rejected:
      return 'rejected';
    default:
      return 'missing';
  }
}

function describeRowFile(state: RowFileState, fileName: string, file: DisplayFile | undefined): string {
  switch (state) {
    case 'none':
      return FILE_MESSAGES.noFileSelected;
    case 'missing':
      return FILE_MESSAGES.notUploaded(fileName);
    case 'uploading':
      return FILE_MESSAGES.uploading;
    case 'failed':
      return file?.message || FILE_MESSAGES.failed;
    case 'rejected':
      return file?.message || FILE_MESSAGES.notValid;
    default:
      return file?.message ?? '';
  }
}

function getRank(dataErrorCount: number, fileState: RowFileState, dataWarningCount: number, isNewRevision: boolean): number {
  if (dataErrorCount > 0) {
    return 0;
  }
  if (fileState === 'none' || fileState === 'missing' || fileState === 'uploading') {
    return 1;
  }
  if (fileState === 'rejected' || fileState === 'failed') {
    return 2;
  }
  if (dataWarningCount > 0) {
    return 3;
  }
  return isNewRevision ? 4 : 5;
}

function getStatus(rank: number, fileState: RowFileState): { label: string; icon: string } {
  switch (rank) {
    case 0:
      return { label: 'Needs correction', icon: 'error' };
    case 1:
      return fileState === 'uploading' ? { label: 'Uploading', icon: 'sync' } : { label: 'Needs a file', icon: 'attach_file' };
    case 2:
      return fileState === 'failed' ? { label: 'Upload failed', icon: 'cloud_off' } : { label: 'File not valid', icon: 'block' };
    case 3:
      return { label: 'Review', icon: 'warning' };
    case 4:
      return { label: 'New revision', icon: 'update' };
    default:
      return { label: 'Ready', icon: 'check_circle' };
  }
}

function isSameView(previous: RowView, next: RowView): boolean {
  return previous.row === next.row
    && previous.excelRow === next.excelRow
    && previous.fileName === next.fileName
    && previous.fileState === next.fileState
    && previous.fileMessage === next.fileMessage
    && previous.sharedCount === next.sharedCount
    && previous.dataErrorCount === next.dataErrorCount
    && previous.dataWarningCount === next.dataWarningCount
    && previous.revisionMessage === next.revisionMessage;
}

/**
 * Builds the view of every row in one pass. A view whose inputs did not change keeps its previous object, so one file changing state
 * does not recreate the other rows (and the table does not re-render them).
 * @param rows The rows in the order the server stores them.
 * @param fileNameColumnIndex Index of the file name column, or -1 when the index has none.
 * @param filesByKey The page's view of each file, by normalized name.
 * @param getRowIssues The validation issues of one row.
 * @param previous The views of the previous pass, by row ID.
 */
export function buildRowViews(
  rows: ReadonlyArray<PreviewRow>,
  fileNameColumnIndex: number,
  filesByKey: ReadonlyMap<string, DisplayFile>,
  getRowIssues: (rowId: number) => ReadonlyArray<DocumentUploadIssueViewModel>,
  previous: ReadonlyMap<number, RowView> = new Map<number, RowView>(),
): RowView[] {
  const usageByKey: Map<string, number> = new Map<string, number>();
  const fileNames: string[] = rows.map((row: PreviewRow) => (fileNameColumnIndex >= 0 ? (row.values[fileNameColumnIndex] ?? '').trim() : ''));
  for (const fileName of fileNames) {
    const key: string = normalizeFileName(fileName);
    if (key) {
      usageByKey.set(key, (usageByKey.get(key) ?? 0) + 1);
    }
  }

  return rows.map((row: PreviewRow, index: number): RowView => {
    const fileName: string = fileNames[index];
    const fileKey: string = normalizeFileName(fileName);
    const file: DisplayFile | undefined = fileKey ? filesByKey.get(fileKey) : undefined;
    const fileState: RowFileState = toRowFileState(fileKey.length > 0, file);

    let dataErrorCount: number = 0;
    let dataWarningCount: number = 0;
    let revisionMessage: string = '';
    for (const issue of getRowIssues(row.rowId)) {
      if (issue.columnKey === FILE_NAME_COLUMN_KEY) {
        continue;
      }
      if (issue.severity === DocumentUploadIssueSeverity.Error) {
        dataErrorCount++;
      } else if (issue.severity === DocumentUploadIssueSeverity.Warning) {
        dataWarningCount++;
      } else if (issue.severity === DocumentUploadIssueSeverity.Information) {
        revisionMessage = issue.message ?? '';
      }
    }

    const isNewRevision: boolean = revisionMessage.length > 0;
    const rank: number = getRank(dataErrorCount, fileState, dataWarningCount, isNewRevision);
    const status: { label: string; icon: string } = getStatus(rank, fileState);
    const view: RowView = {
      row,
      excelRow: row.rowId,
      fileName,
      fileKey,
      fileState,
      fileMessage: describeRowFile(fileState, fileName, file),
      sharedCount: fileKey ? usageByKey.get(fileKey) ?? 1 : 0,
      dataErrorCount,
      dataWarningCount,
      isNewRevision,
      revisionMessage,
      severity: rank === 0 || rank === 2 || fileState === 'none' || fileState === 'missing' ? 'error' : rank === 3 ? 'warning' : 'none',
      rank,
      statusLabel: status.label,
      statusIcon: status.icon,
    };

    const earlier: RowView | undefined = previous.get(row.rowId);
    return earlier && isSameView(earlier, view) ? earlier : view;
  });
}

function compareText(first: string, second: string): number {
  return first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Orders the rows for presentation only; the rows themselves, their IDs and the server's order are never changed.
 * Without a user sort the rows with problems come first (data errors, missing file, rejected or failed file, warnings, healthy rows),
 * and the Excel row breaks every tie so the order is stable.
 * @param views The row views.
 * @param sort What the user chose; an empty direction means the default order.
 * @param columnKeys The keys of the index columns, aligned with each row's `values`.
 */
export function sortRowViews(views: ReadonlyArray<RowView>, sort: RowSort, columnKeys: ReadonlyArray<string>): RowView[] {
  const byExcelRow = (a: RowView, b: RowView): number => a.excelRow - b.excelRow;
  if (!sort.direction || !sort.active) {
    return [...views].sort((a: RowView, b: RowView) => a.rank - b.rank || byExcelRow(a, b));
  }

  const direction: number = sort.direction === 'desc' ? -1 : 1;
  const columnIndex: number = columnKeys.indexOf(sort.active);
  const primary = (a: RowView, b: RowView): number => {
    switch (sort.active) {
      case 'status':
        return a.rank - b.rank;
      case 'excelRow':
        return a.excelRow - b.excelRow;
      default:
        return columnIndex < 0 ? 0 : compareText(a.row.values[columnIndex] ?? '', b.row.values[columnIndex] ?? '');
    }
  };
  return [...views].sort((a: RowView, b: RowView) => direction * primary(a, b) || byExcelRow(a, b));
}

/** The informational line about rows that create a new release of a current record; it is not an issue. */
export function describeNewRevisions(count: number): string {
  return count === 1 ? '1 row will create a new revision.' : `${count} rows will create new revisions.`;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

/**
 * The number of records of one document type in words, e.g. `1 Valve record` or `2 Valve records`.
 * @param documentTypeLabel The visible name of the type (`Pipe Class` or `Valve`).
 * @param count How many records.
 */
export function describeRecordCount(documentTypeLabel: string, count: number): string {
  return `${count} ${documentTypeLabel} ${plural(count, 'record', 'records')}`;
}

/**
 * Groups everything that needs the user's attention by kind, with one line per kind. The page never lists each issue here.
 * `isHealthy` is true only when no kind has anything in it, so a valid index alone never shows a healthy state.
 * @param inputs The counts, taken from the rows, the files and the validation.
 */
export function buildReviewSummary(inputs: ReviewInputs): ReviewSummary {
  const categories: ReviewCategory[] = [];
  const add = (id: string, severity: ReviewCategory['severity'], count: number, text: string): void => {
    if (count > 0) {
      categories.push({ id, severity, count, text });
    }
  };

  add('rowsNeedingFile', 'error', inputs.rowsNeedingFile,
    `${inputs.rowsNeedingFile} ${plural(inputs.rowsNeedingFile, 'row requires', 'rows require')} a file.`);
  add('rowsWithErrors', 'error', inputs.rowsWithErrors,
    `${inputs.rowsWithErrors} ${plural(inputs.rowsWithErrors, 'row contains values', 'rows contain values')} that must be corrected.`);
  add('filesFailed', 'error', inputs.filesFailed,
    `${inputs.filesFailed} ${plural(inputs.filesFailed, 'file', 'files')} could not be uploaded.`);
  add('filesNotValid', 'error', inputs.filesNotValid,
    `${inputs.filesNotValid} ${plural(inputs.filesNotValid, 'file is', 'files are')} not valid.`);
  add('filesNotReferenced', 'warning', inputs.filesNotReferenced,
    `${inputs.filesNotReferenced} selected ${plural(inputs.filesNotReferenced, 'file is', 'files are')} not referenced by the index and ${plural(inputs.filesNotReferenced, 'was', 'were')} not uploaded.`);
  add('filesRepeated', 'error', inputs.filesRepeated,
    `${inputs.filesRepeated} selected file ${plural(inputs.filesRepeated, 'name was', 'names were')} repeated, so ${plural(inputs.filesRepeated, 'it was', 'they were')} not uploaded.`);
  add('filesNoLongerUsed', 'warning', inputs.filesNoLongerUsed,
    `${inputs.filesNoLongerUsed} uploaded ${plural(inputs.filesNoLongerUsed, 'file is', 'files are')} no longer referenced by the index.`);
  add('otherIssues', 'error', inputs.otherIssues,
    `${inputs.otherIssues} other ${plural(inputs.otherIssues, 'issue', 'issues')} must be resolved.`);
  add('warnings', 'warning', inputs.warningCount,
    `${inputs.warningCount} ${plural(inputs.warningCount, 'warning', 'warnings')} should be reviewed.`);
  add('filesUploading', 'info', inputs.filesUploading,
    `${inputs.filesUploading} ${plural(inputs.filesUploading, 'file is', 'files are')} still uploading.`);

  return {
    categories,
    issueCount: categories.filter((category: ReviewCategory) => category.severity !== 'info').reduce((sum: number, category: ReviewCategory) => sum + category.count, 0),
    isHealthy: categories.length === 0,
  };
}

/** A file the user can pick for a row: one the page holds but never uploaded, or one that is already uploaded. */
export interface FileCandidate {
  key: string;
  fileName: string;
  isUploaded: boolean;
  /** How many rows use the file now; 0 for a file no row refers to. */
  usedByRows: number;
}

/** What the file picker is opened with. */
export interface FilePickerData {
  candidates: FileCandidate[];
}

/** Files whose name contains every word of `search`. */
export function filterFileCandidates(candidates: ReadonlyArray<FileCandidate>, search: string): FileCandidate[] {
  const words: string[] = search.toLowerCase().split(/\s+/).filter((word: string) => word.length > 0);
  return candidates.filter((candidate: FileCandidate) => words.every((word: string) => candidate.fileName.toLowerCase().includes(word)));
}

/** The rows the user can pick when associating a file. */
export interface RowCandidate {
  rowId: number;
  excelRow: number;
  identifier: string;
  businessUnit: string;
  fileName: string;
  issue: string;
}

/** The answer of the row picker: the rows to use, and whether to add a new row for the file as well. */
export interface RowPickerResult {
  rowIds: number[];
  addNewRow: boolean;
}

/** What the row picker is opened with. */
export interface RowPickerData {
  fileName: string;
  candidates: RowCandidate[];
}

/** Rows that match every word of `search` in their Excel row, name or tag, business unit, file name or state. */
export function filterCandidates(candidates: ReadonlyArray<RowCandidate>, search: string): RowCandidate[] {
  const words: string[] = search.toLowerCase().split(/\s+/).filter((word: string) => word.length > 0);
  if (words.length === 0) {
    return [...candidates];
  }
  return candidates.filter((candidate: RowCandidate) => {
    const haystack: string = `${candidate.excelRow} ${candidate.identifier} ${candidate.businessUnit} ${candidate.fileName} ${candidate.issue}`.toLowerCase();
    return words.every((word: string) => haystack.includes(word));
  });
}
